import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TravelWaiver } from '../components/TravelWaiver';
import { TravelerDetailsForm } from '../components/TravelerDetailsForm';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Calendar,
  MapPin,
  Users,
  FileCheck,
  Mail,
  ArrowRight,
  Plane,
  ClipboardList
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingSuccessPage() {
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('details'); // 'details', 'waiver', 'complete'
  const [savingDetails, setSavingDetails] = useState(false);
  const [savingWaiver, setSavingWaiver] = useState(false);

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
    } else {
      setLoading(false);
    }
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const res = await axios.get(`${API}/bookings/public/${bookingId}`);
      setBooking(res.data);
      
      // Check if details and waiver are already completed
      if (res.data.waiver_signed && res.data.traveler_details_completed) {
        setCurrentStep('complete');
      } else if (res.data.traveler_details_completed) {
        setCurrentStep('waiver');
      }
    } catch (err) {
      console.error('Failed to fetch booking:', err);
      toast.error('Could not load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsSubmit = async (detailsData) => {
    setSavingDetails(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/details`, {
        traveler_info: detailsData
      });
      setCurrentStep('waiver');
      toast.success('Details saved! Now please sign the waiver.');
    } catch (err) {
      console.error('Failed to save details:', err);
      toast.error('Failed to save details. Please try again.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleWaiverAccept = async (waiverData) => {
    setSavingWaiver(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/waiver`, {
        waiver: waiverData
      });
      setCurrentStep('complete');
      toast.success('Booking complete! You\'re all set for your adventure.');
    } catch (err) {
      console.error('Failed to save waiver:', err);
      toast.error('Failed to save waiver. Please try again.');
    } finally {
      setSavingWaiver(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-primary mb-3">
            Payment Successful!
          </h1>
          <p className="text-lg text-muted-foreground">
            Thank you for booking with Circulate Travel
          </p>
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center justify-center gap-2">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${currentStep === 'details' ? 'bg-secondary text-white' : currentStep === 'waiver' || currentStep === 'complete' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">
                {currentStep === 'waiver' || currentStep === 'complete' ? '✓' : '1'}
              </span>
              <span className="text-sm font-medium">Traveler Details</span>
            </div>
            <div className="w-8 h-0.5 bg-muted" />
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${currentStep === 'waiver' ? 'bg-secondary text-white' : currentStep === 'complete' ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">
                {currentStep === 'complete' ? '✓' : '2'}
              </span>
              <span className="text-sm font-medium">Sign Waiver</span>
            </div>
          </div>
        </motion.div>

        {/* Booking Details Card */}
        {booking && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="h-5 w-5 text-secondary" />
                  Booking Confirmation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-secondary/10 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Confirmation Number</p>
                  <p className="font-mono text-lg font-bold text-primary">{bookingId?.slice(0, 8).toUpperCase()}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Trip</p>
                      <p className="font-medium">{booking.trip_title || 'Your Adventure'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{booking.tour_date}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Travelers</p>
                      <p className="font-medium">{booking.travelers || 1}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-secondary mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{booking.guest_email}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="border-t pt-4 mt-4 space-y-3">
                  {booking.payment_plan && booking.payment_plan !== 'full' ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Payment Plan</span>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {booking.total_payments} Monthly Payments
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Today's Payment (1 of {booking.total_payments})</span>
                        <span className="text-xl font-bold text-green-600">
                          ${booking.monthly_payment?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Remaining Balance</span>
                        <span className="font-medium text-orange-600">
                          ${booking.remaining_amount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm border-t pt-2">
                        <span className="text-muted-foreground">Trip Total</span>
                        <span className="font-medium">
                          ${booking.total_amount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                        <strong>Next payment:</strong> ${booking.monthly_payment?.toLocaleString()} due in 30 days. 
                        You'll receive a reminder email before each payment.
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Total Paid</span>
                      <span className="text-2xl font-bold text-green-600">
                        ${booking.total_amount?.toLocaleString() || '0'}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {currentStep === 'details' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-secondary" />
                  Complete Your Traveler Information
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Please provide the following details to finalize your booking.
                </p>
              </CardHeader>
              <CardContent>
                <TravelerDetailsForm
                  tripType={booking?.trip_type || 'both'}
                  onSubmit={handleDetailsSubmit}
                  loading={savingDetails}
                />
              </CardContent>
            </Card>
          )}

          {currentStep === 'waiver' && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <FileCheck className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg text-primary">Travel Waiver</h3>
                      <Badge className="bg-orange-500">Required</Badge>
                    </div>
                    <p className="text-muted-foreground mb-4">
                      Please sign the travel waiver to complete your booking.
                    </p>
                    <Button 
                      onClick={() => {}}
                      className="bg-secondary hover:bg-secondary/90"
                      data-testid="sign-waiver-btn"
                    >
                      Sign Waiver Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {currentStep === 'complete' && (
            <>
              <Card className="border-green-200 bg-green-50/50 mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-primary mb-1">Booking Complete!</h3>
                      <p className="text-muted-foreground">
                        Your traveler details and waiver have been submitted. You're all set for your adventure!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* What's Next Section */}
              <Card>
                <CardHeader>
                  <CardTitle>What's Next?</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">✓</span>
                      <div>
                        <p className="font-medium text-green-700">Payment received</p>
                        <p className="text-sm text-muted-foreground">Your booking is confirmed</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">✓</span>
                      <div>
                        <p className="font-medium text-green-700">Traveler details submitted</p>
                        <p className="text-sm text-muted-foreground">Passport, emergency contact, and other info saved</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">✓</span>
                      <div>
                        <p className="font-medium text-green-700">Travel waiver signed</p>
                        <p className="text-sm text-muted-foreground">All legal documents complete</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground">4</span>
                      <div>
                        <p className="font-medium">Check your email</p>
                        <p className="text-sm text-muted-foreground">Confirmation and trip details sent to your inbox</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground">5</span>
                      <div>
                        <p className="font-medium">Prepare for your adventure</p>
                        <p className="text-sm text-muted-foreground">Review packing lists and trip guides</p>
                      </div>
                    </li>
                  </ol>
                </CardContent>
              </Card>
            </>
          )}
        </motion.div>

        {/* Action Buttons */}
        {currentStep === 'complete' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button asChild variant="outline" size="lg">
              <Link to="/trips">Browse More Trips</Link>
            </Button>
            <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90">
              <Link to="/">Return to Home</Link>
            </Button>
          </motion.div>
        )}
      </div>

      {/* Waiver Modal */}
      {currentStep === 'waiver' && (
        <TravelWaiver
          tripTitle={booking?.trip_title || 'Your Trip'}
          onAccept={handleWaiverAccept}
          onCancel={() => setCurrentStep('details')}
          loading={savingWaiver}
        />
      )}
    </div>
  );
}
